import React from "react";
import "./Footer.css";
import { Link } from "react-router-dom";
import { scrollPageToTop } from "../ScrollToTop";
import { useSignInModal } from "../shared/SignInModalContext";

const LinkedInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 1.881 4.267 4.329v6.412zM5.337 7.433a2.062 2.062 0 1 1-.004-4.125 2.062 2.062 0 0 1 .004 4.125zM7.119 20.452H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.727-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
  </svg>
);

const footerScrollToTop = () => scrollPageToTop();

export default function Footer() {
  const { openSignIn } = useSignInModal();

  return (
    <footer className="footer">
      {/* Mobile / tablet: existing footer */}
      <div className="footer-legacy">
        <div className="footer-main">
          <div className="footer-col brand-col">
            <Link to="/" className="footer-brand-link" aria-label="Highwatch AI Home">
              <img
                src="/icons/Highwatch_logo.svg"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/icons/Highwatch_logo.svg";
                }}
                alt="Highwatch AI"
                className="footer-legacy__logo-img"
              />
            </Link>
            <div className="brand-lines">
              <span className="brand-subtitle">The Trusted AI Brain for Regulated Enterprises</span>
              <p className="footer-subtext">
                Secure, compliant, and deeply integrated intelligence across your systems.
              </p>
              <p className="footer-subtext">📍 Headquarters: New York, NY</p>
              <p className="footer-subtext">📧 future@highwatch.ai</p>
            </div>
          </div>

          <div className="footer-col">
            <h4>Explore</h4>
            <ul className="explore-links">
              <li>
                <Link to="/" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}>
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/ProductInsurance"
                  onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
                >
                  Product
                </Link>
              </li>
              <li>
                <Link
                  to="/Solutions"
                  onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
                >
                  Use Cases
                </Link>
              </li>
              <li>
                <Link
                  to="/Pricing"
                  onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
                >
                  Pricing
                </Link>
              </li>
              <li>
                <Link
                  to="/AboutUs"
                  onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  to="/Contact"
                  onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          <div className="footer-col">
            <h4>Trust & Compliance</h4>
            <div className="compliance-subheading">Compliance & Standards</div>
            <ul className="compliance-list">
              <li className="footer-subtext">
                SOC 2–Aligned Controls (Type I at launch; Type II on roadmap)
              </li>
              <li className="footer-subtext">HIPAA, GDPR, NAIC, and EU AI Act Readiness</li>
              <li className="footer-subtext">
                Enterprise-Grade Encryption (AES-256 at rest, TLS 1.3 in transit)
              </li>
            </ul>
            <div className="security-note" role="note">
              <div className="label">Security Note</div>
              <div className="text">
                Your data is never used for model training without explicit consent.
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom" style={{ borderTop: "1px solid var(--corelink-divider)" }}>
          <div className="left">
            <div>© 2025 Highwatch AI, Inc. All rights reserved.</div>
            <div>Built for Trust · Compliance · Intelligence</div>
          </div>
          <div className="right" aria-label="Social links">
            <a
              className="social-btn"
              href="https://www.linkedin.com/company/corelink-ai/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <LinkedInIcon />
            </a>
          </div>
        </div>
      </div>

      {/* Desktop web: new Figma footer */}
      <div className="footer-figma">
        <div className="footer-figma__main">
          <div className="footer-figma__brand">
            <Link to="/" className="footer-figma__logo" aria-label="Highwatch AI Home" onClick={footerScrollToTop}>
              <img
                src="/icons/Highwatch_logo.svg"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/icons/Highwatch_logo.svg";
                }}
                alt="Highwatch AI"
                className="footer-figma__logo-img"
              />
            </Link>
            <p>
              The intelligence infrastructure of the modern enterprise. Built for regulated
              industries from day one.
            </p>
            <div className="footer-figma__social">
              <a
                href="https://www.linkedin.com/company/corelink-ai/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <LinkedInIcon />
              </a>
              <a
                href="https://x.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
              >
                <XIcon />
              </a>
            </div>
          </div>

          <div className="footer-figma__col">
            <h4>Company</h4>
            <ul>
              <li>
                <Link to="/" onClick={footerScrollToTop}>Home</Link>
              </li>
              <li>
                <Link to="/Solutions" onClick={footerScrollToTop}>Solutions</Link>
              </li>
              <li>
                <Link to="/Pricing" onClick={footerScrollToTop}>Pricing</Link>
              </li>
              <li>
                <Link to="/AboutUs" onClick={footerScrollToTop}>About</Link>
              </li>
              <li>
                <Link to="/Contact" onClick={footerScrollToTop}>Contact</Link>
              </li>
            </ul>
          </div>

          <div className="footer-figma__col">
            <h4>Products</h4>
            <ul>
              <li>
                <Link to="/ProductIntelligence" onClick={footerScrollToTop}>Intelligence</Link>
              </li>
              <li>
                <Link to="/ProductInsurance" onClick={footerScrollToTop}>Insurance</Link>
              </li>
              <li>
                <span className="footer-figma__soon">
                  Finance <em>Soon</em>
                </span>
              </li>
              <li>
                <span className="footer-figma__soon">
                  Healthcare <em>Soon</em>
                </span>
              </li>
            </ul>
          </div>

          <div className="footer-figma__col">
            <h4>Get Started</h4>
            <ul>
              <li>
                <Link to="/Contact" onClick={footerScrollToTop}>Get a demo</Link>
              </li>
              <li>
                <button type="button" className="footer-figma__link-btn" onClick={openSignIn}>
                  Sign in
                </button>
              </li>
              <li>
                <Link to="/Contact" onClick={footerScrollToTop}>Talk to sales</Link>
              </li>
              <li>
                <Link to="/Contact" onClick={footerScrollToTop}>For investors</Link>
              </li>
            </ul>
          </div>

          <div className="footer-figma__col footer-figma__col--trust">
            <h4>Trust & Compliance</h4>
            <div className="footer-figma__trust-title">Compliance & Standards</div>
            <p>SOC 2–Aligned Controls (Type I at launch; Type II on roadmap)</p>
            <p>HIPAA, GDPR, NAIC, and EU AI Act Readiness</p>
            <p>Enterprise-Grade Encryption (AES-256 at rest, TLS 1.3 in transit)</p>
          </div>
        </div>

        <div className="footer-figma__bottom">
          <span>© 2026 Highwatch AI. All rights reserved.</span>
          <div className="footer-figma__legal">
            <Link to="/PrivacyPolicy" onClick={footerScrollToTop}>Privacy policy</Link>
            <Link to="/TermsOfService" onClick={footerScrollToTop}>Terms of service</Link>
            <Link to="/Contact" onClick={footerScrollToTop}>Security</Link>
          </div>
          <span className="footer-figma__tagline">High Trust. High Performance. Highwatch.</span>
        </div>
      </div>
    </footer>
  );
}
